import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, startOfMonth, endOfMonth, subMonths, subYears, startOfYear, endOfYear, isWithinInterval, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import {
  Package,
  RefreshCcw,
  Layers,
  Tags,
  Clock,
  Check,
  FileText,
  ListOrdered,
  CalendarDays,
  CalendarClock,
  CalendarRange,
  Receipt,
  History,
} from 'lucide-react';
import { DashboardHeaderMorph } from '../components/dashboard/DashboardHeaderMorph';
import { API } from '@/lib/apiBase';
import { filterActiveCategories } from '@/lib/categoryUtils';
import { isProductActive } from '@/lib/productUtils';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount || 0);

const parseEntryDate = (dateStr) => {
  if (!dateStr) return null;
  const parsed = parseISO(String(dateStr).slice(0, 10));
  return isValid(parsed) ? parsed : null;
};

const countLinesInPeriod = (entries, start, end) =>
  entries.reduce((sum, entry) => {
    const date = parseEntryDate(entry.invoice_date);
    if (!date || !isWithinInterval(date, { start, end })) return sum;
    return sum + (entry.lines?.length || 0);
  }, 0);

const countInvoicesInPeriod = (entries, start, end) =>
  entries.filter((entry) => {
    const date = parseEntryDate(entry.invoice_date);
    return date && isWithinInterval(date, { start, end });
  }).length;

const SummaryCard = ({
  icon: Icon,
  label,
  value,
  subtext,
  period,
  amountHt,
  loading: subtextLoading,
  iconClassName,
  iconBgClassName,
  to,
}) => {
  const navigate = useNavigate();

  const renderFooter = () => {
    if (subtextLoading) {
      return <p className="text-[9px] text-muted-foreground mt-2">Chargement…</p>;
    }
    if (period) {
      return (
        <p className="text-[9px] mt-2 line-clamp-2 leading-snug">
          <span className="font-semibold text-primary">{period}</span>
          {amountHt != null && (
            <>
              <span className="text-muted-foreground"> - </span>
              <span className="font-semibold text-primary">{formatCurrency(amountHt)} HT</span>
            </>
          )}
        </p>
      );
    }
    if (subtext) {
      return (
        <p className="text-[9px] text-muted-foreground mt-2 line-clamp-2 leading-snug">{subtext}</p>
      );
    }
    return null;
  };

  return (
    <Card
      className={`bg-card/80 border-border card-hover aspect-square w-36 sm:w-40 shrink-0${to ? ' cursor-pointer hover:border-primary/40 transition-colors' : ''}`}
      onClick={to ? () => navigate(to) : undefined}
    >
      <CardContent className="p-3.5 sm:p-4 h-full flex flex-col">
        <div className={`p-1.5 rounded-lg w-fit ${iconBgClassName}`}>
          <Icon className={`w-3.5 h-3.5 ${iconClassName}`} />
        </div>
        <p className="text-[8px] sm:text-[8px] font-medium uppercase tracking-wide text-muted-foreground mt-2 line-clamp-3 leading-[1.15]">
          {label}
        </p>
        <p className="text-xl sm:text-2xl font-bold text-foreground mt-auto font-mono tabular-nums leading-none truncate">
          {value}
        </p>
        {renderFooter()}
      </CardContent>
    </Card>
  );
};

const DashboardSection = ({ title, icon: Icon, iconClassName, children, contentClassName = 'flex flex-wrap gap-4' }) => (
  <section className="space-y-4">
    <div className="flex items-center gap-2">
      <Icon className={`w-5 h-5 ${iconClassName}`} />
      <h2 className="font-heading text-lg font-semibold text-foreground">{title}</h2>
    </div>
    <div className={contentClassName}>{children}</div>
  </section>
);

export const Dashboard = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [refacturationLines, setRefacturationLines] = useState([]);
  const [entries, setEntries] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const [productsRes, categoriesRes, linesRes, entriesRes] = await Promise.all([
        axios.get(`${API}/products`, {
          params: { include_inactive: true },
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/categories`, {
          params: { include_inactive: true },
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/entry-lines/all`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/entries`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setProducts(productsRes.data || []);
      setCategories(categoriesRes.data || []);
      setEntries(entriesRes.data || []);

      const refacturableLines = (linesRes.data || []).filter(
        (line) =>
          line.payer_company_name &&
          line.company_name &&
          line.payer_company_name !== line.company_name,
      );
      setRefacturationLines(refacturableLines);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const activeProductsCount = useMemo(
    () => products.filter(isProductActive).length,
    [products],
  );

  const activeCategoriesCount = useMemo(
    () => filterActiveCategories(categories).length,
    [categories],
  );

  const refacturationStats = useMemo(() => {
    const now = new Date();
    const currentMonthStart = startOfMonth(now);
    const currentMonthEnd = endOfMonth(now);
    const currentMonthLabelRaw = format(now, 'MMMM yyyy', { locale: fr });
    const currentMonthLabel =
      currentMonthLabelRaw.charAt(0).toUpperCase() + currentMonthLabelRaw.slice(1);
    const previousMonth = subMonths(now, 1);
    const previousMonthStart = startOfMonth(previousMonth);
    const previousMonthEnd = endOfMonth(previousMonth);
    const previousMonthLabelRaw = format(previousMonth, 'MMMM yyyy', { locale: fr });
    const previousMonthLabel =
      previousMonthLabelRaw.charAt(0).toUpperCase() + previousMonthLabelRaw.slice(1);
    const yearStart = startOfYear(now);
    const yearEnd = endOfYear(now);
    const yearLabel = format(now, 'yyyy');
    const previousYear = subYears(now, 1);
    const previousYearStart = startOfYear(previousYear);
    const previousYearEnd = endOfYear(previousYear);
    const previousYearLabel = format(previousYear, 'yyyy');

    const pendingLines = refacturationLines.filter(
      (line) => line.refacturation_status === 'pending',
    );
    const pendingCurrentMonthLines = pendingLines.filter((line) => {
      const date = parseEntryDate(line.invoice_date);
      return date && isWithinInterval(date, { start: currentMonthStart, end: currentMonthEnd });
    });
    const pendingPreviousMonthLines = pendingLines.filter((line) => {
      const date = parseEntryDate(line.invoice_date);
      return date && isWithinInterval(date, { start: previousMonthStart, end: previousMonthEnd });
    });
    const pendingCurrentYearLines = pendingLines.filter((line) => {
      const date = parseEntryDate(line.invoice_date);
      return date && isWithinInterval(date, { start: yearStart, end: yearEnd });
    });
    const pendingPreviousYearLines = pendingLines.filter((line) => {
      const date = parseEntryDate(line.invoice_date);
      return date && isWithinInterval(date, { start: previousYearStart, end: previousYearEnd });
    });
    const refacturedLines = refacturationLines.filter(
      (line) => line.refacturation_status === 'refactured',
    );
    const refacturedCurrentMonthLines = refacturedLines.filter((line) => {
      const date = parseEntryDate(line.invoice_date);
      return date && isWithinInterval(date, { start: currentMonthStart, end: currentMonthEnd });
    });
    const refacturedPreviousMonthLines = refacturedLines.filter((line) => {
      const date = parseEntryDate(line.invoice_date);
      return date && isWithinInterval(date, { start: previousMonthStart, end: previousMonthEnd });
    });
    const refacturedCurrentYearLines = refacturedLines.filter((line) => {
      const date = parseEntryDate(line.invoice_date);
      return date && isWithinInterval(date, { start: yearStart, end: yearEnd });
    });
    const refacturedPreviousYearLines = refacturedLines.filter((line) => {
      const date = parseEntryDate(line.invoice_date);
      return date && isWithinInterval(date, { start: previousYearStart, end: previousYearEnd });
    });

    return {
      pendingCurrentMonthCount: pendingCurrentMonthLines.length,
      pendingCurrentMonthHt: pendingCurrentMonthLines.reduce(
        (sum, line) => sum + (line.amount_ht || 0),
        0,
      ),
      pendingPreviousMonthCount: pendingPreviousMonthLines.length,
      pendingPreviousMonthHt: pendingPreviousMonthLines.reduce(
        (sum, line) => sum + (line.amount_ht || 0),
        0,
      ),
      pendingCurrentYearCount: pendingCurrentYearLines.length,
      pendingCurrentYearHt: pendingCurrentYearLines.reduce(
        (sum, line) => sum + (line.amount_ht || 0),
        0,
      ),
      pendingPreviousYearCount: pendingPreviousYearLines.length,
      pendingPreviousYearHt: pendingPreviousYearLines.reduce(
        (sum, line) => sum + (line.amount_ht || 0),
        0,
      ),
      refacturedCurrentMonthCount: refacturedCurrentMonthLines.length,
      refacturedCurrentMonthHt: refacturedCurrentMonthLines.reduce(
        (sum, line) => sum + (line.amount_ht || 0),
        0,
      ),
      refacturedPreviousMonthCount: refacturedPreviousMonthLines.length,
      refacturedPreviousMonthHt: refacturedPreviousMonthLines.reduce(
        (sum, line) => sum + (line.amount_ht || 0),
        0,
      ),
      refacturedCurrentYearCount: refacturedCurrentYearLines.length,
      refacturedCurrentYearHt: refacturedCurrentYearLines.reduce(
        (sum, line) => sum + (line.amount_ht || 0),
        0,
      ),
      refacturedPreviousYearCount: refacturedPreviousYearLines.length,
      refacturedPreviousYearHt: refacturedPreviousYearLines.reduce(
        (sum, line) => sum + (line.amount_ht || 0),
        0,
      ),
      currentMonthLabel,
      previousMonthLabel,
      yearLabel,
      previousYearLabel,
      currentMonthDateFrom: format(currentMonthStart, 'yyyy-MM-dd'),
      currentMonthDateTo: format(currentMonthEnd, 'yyyy-MM-dd'),
      previousMonthDateFrom: format(previousMonthStart, 'yyyy-MM-dd'),
      previousMonthDateTo: format(previousMonthEnd, 'yyyy-MM-dd'),
      yearDateFrom: format(yearStart, 'yyyy-MM-dd'),
      yearDateTo: format(yearEnd, 'yyyy-MM-dd'),
      previousYearDateFrom: format(previousYearStart, 'yyyy-MM-dd'),
      previousYearDateTo: format(previousYearEnd, 'yyyy-MM-dd'),
    };
  }, [refacturationLines]);

  const entriesStats = useMemo(() => {
    const now = new Date();
    const currentMonthStart = startOfMonth(now);
    const currentMonthEnd = endOfMonth(now);
    const previousMonth = subMonths(now, 1);
    const previousMonthStart = startOfMonth(previousMonth);
    const previousMonthEnd = endOfMonth(previousMonth);
    const yearStart = startOfYear(now);
    const yearEnd = endOfYear(now);
    const previousYear = subYears(now, 1);
    const previousYearStart = startOfYear(previousYear);
    const previousYearEnd = endOfYear(previousYear);

    const currentMonthLabel = format(now, 'MMMM yyyy', { locale: fr });
    const previousMonthLabel = format(previousMonth, 'MMMM yyyy', { locale: fr });
    const yearLabel = format(now, 'yyyy');
    const previousYearLabel = format(previousYear, 'yyyy');

    return {
      linesCurrentMonth: countLinesInPeriod(entries, currentMonthStart, currentMonthEnd),
      linesPreviousMonth: countLinesInPeriod(entries, previousMonthStart, previousMonthEnd),
      linesCurrentYear: countLinesInPeriod(entries, yearStart, yearEnd),
      linesPreviousYear: countLinesInPeriod(entries, previousYearStart, previousYearEnd),
      invoicesCurrentMonth: countInvoicesInPeriod(entries, currentMonthStart, currentMonthEnd),
      invoicesPreviousMonth: countInvoicesInPeriod(entries, previousMonthStart, previousMonthEnd),
      invoicesCurrentYear: countInvoicesInPeriod(entries, yearStart, yearEnd),
      invoicesPreviousYear: countInvoicesInPeriod(entries, previousYearStart, previousYearEnd),
      currentMonthLabel: currentMonthLabel.charAt(0).toUpperCase() + currentMonthLabel.slice(1),
      previousMonthLabel: previousMonthLabel.charAt(0).toUpperCase() + previousMonthLabel.slice(1),
      yearLabel,
      previousYearLabel,
      currentMonthDateFrom: format(currentMonthStart, 'yyyy-MM-dd'),
      currentMonthDateTo: format(currentMonthEnd, 'yyyy-MM-dd'),
      previousMonthDateFrom: format(previousMonthStart, 'yyyy-MM-dd'),
      previousMonthDateTo: format(previousMonthEnd, 'yyyy-MM-dd'),
      yearDateFrom: format(yearStart, 'yyyy-MM-dd'),
      yearDateTo: format(yearEnd, 'yyyy-MM-dd'),
      previousYearDateFrom: format(previousYearStart, 'yyyy-MM-dd'),
      previousYearDateTo: format(previousYearEnd, 'yyyy-MM-dd'),
    };
  }, [entries]);

  const displayCount = (count) => (loading ? '—' : count);

  const firstName = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'Utilisateur';
  const formattedDate = format(new Date(), "EEEE d MMMM", { locale: fr });
  const capitalizedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="relative overflow-visible rounded-2xl border border-border bg-gradient-to-br from-card via-card to-secondary/30 p-6 md:p-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="space-y-2 max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">
              Tableau de bord
            </p>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
              Bonjour, {firstName}
            </h1>
            <p className="text-muted-foreground">
              {capitalizedDate} — Consultez la synthèse de vos produits et refacturations.
            </p>
          </div>

          <DashboardHeaderMorph />
        </div>
      </div>

      <div className="space-y-10">
        <div className="flex flex-col xl:flex-row xl:items-start gap-10 xl:gap-[12.5rem]">
          <DashboardSection title="Produits" icon={Package} iconClassName="text-primary">
            <SummaryCard
              icon={Layers}
              label="Produits actifs"
              value={displayCount(activeProductsCount)}
              subtext="Produits avec statut activé"
              iconClassName="text-primary"
              iconBgClassName="bg-primary/10"
            />
            <SummaryCard
              icon={Tags}
              label="Catégories actives"
              value={displayCount(activeCategoriesCount)}
              subtext="Catégories avec statut activé"
              iconClassName="text-teal-500"
              iconBgClassName="bg-teal-500/10"
            />
          </DashboardSection>

          <DashboardSection title="Entrées" icon={FileText} iconClassName="text-blue-400" contentClassName="space-y-4">
            <div className="flex flex-wrap gap-4">
              <SummaryCard
                icon={ListOrdered}
                label="Entrées du mois en cours"
                value={displayCount(entriesStats.linesCurrentMonth)}
                period={entriesStats.currentMonthLabel}
                iconClassName="text-blue-400"
                iconBgClassName="bg-blue-400/10"
                to={`/entries?date_from=${entriesStats.currentMonthDateFrom}&date_to=${entriesStats.currentMonthDateTo}`}
              />
              <SummaryCard
                icon={CalendarClock}
                label="Entrées du mois précédent"
                value={displayCount(entriesStats.linesPreviousMonth)}
                period={entriesStats.previousMonthLabel}
                iconClassName="text-indigo-400"
                iconBgClassName="bg-indigo-400/10"
                to={`/entries?date_from=${entriesStats.previousMonthDateFrom}&date_to=${entriesStats.previousMonthDateTo}`}
              />
              <SummaryCard
                icon={CalendarRange}
                label="Entrées de l'année en cours"
                value={displayCount(entriesStats.linesCurrentYear)}
                period={entriesStats.yearLabel}
                iconClassName="text-violet-400"
                iconBgClassName="bg-violet-400/10"
                to={`/entries?date_from=${entriesStats.yearDateFrom}&date_to=${entriesStats.yearDateTo}`}
              />
              <SummaryCard
                icon={History}
                label="Entrées de l'année précédente"
                value={displayCount(entriesStats.linesPreviousYear)}
                period={entriesStats.previousYearLabel}
                iconClassName="text-purple-400"
                iconBgClassName="bg-purple-400/10"
                to={`/entries?date_from=${entriesStats.previousYearDateFrom}&date_to=${entriesStats.previousYearDateTo}`}
              />
            </div>
            <div className="flex flex-wrap gap-4">
              <SummaryCard
                icon={Receipt}
                label="Factures du mois en cours"
                value={displayCount(entriesStats.invoicesCurrentMonth)}
                period={entriesStats.currentMonthLabel}
                iconClassName="text-sky-400"
                iconBgClassName="bg-sky-400/10"
                to={`/entries?date_from=${entriesStats.currentMonthDateFrom}&date_to=${entriesStats.currentMonthDateTo}`}
              />
              <SummaryCard
                icon={CalendarDays}
                label="Factures du mois précédent"
                value={displayCount(entriesStats.invoicesPreviousMonth)}
                period={entriesStats.previousMonthLabel}
                iconClassName="text-cyan-400"
                iconBgClassName="bg-cyan-400/10"
                to={`/entries?date_from=${entriesStats.previousMonthDateFrom}&date_to=${entriesStats.previousMonthDateTo}`}
              />
              <SummaryCard
                icon={FileText}
                label="Factures de l'année en cours"
                value={displayCount(entriesStats.invoicesCurrentYear)}
                period={entriesStats.yearLabel}
                iconClassName="text-emerald-400"
                iconBgClassName="bg-emerald-400/10"
                to={`/entries?date_from=${entriesStats.yearDateFrom}&date_to=${entriesStats.yearDateTo}`}
              />
              <SummaryCard
                icon={History}
                label="Factures de l'année précédente"
                value={displayCount(entriesStats.invoicesPreviousYear)}
                period={entriesStats.previousYearLabel}
                iconClassName="text-lime-400"
                iconBgClassName="bg-lime-400/10"
                to={`/entries?date_from=${entriesStats.previousYearDateFrom}&date_to=${entriesStats.previousYearDateTo}`}
              />
            </div>
          </DashboardSection>
        </div>

        <DashboardSection title="Refacturation" icon={RefreshCcw} iconClassName="text-pink-500" contentClassName="space-y-4">
          <div className="flex flex-wrap gap-4">
            <SummaryCard
              icon={Clock}
              label="Refacturation en attente du mois en cours"
              value={displayCount(refacturationStats.pendingCurrentMonthCount)}
              loading={loading}
              period={refacturationStats.currentMonthLabel}
              amountHt={refacturationStats.pendingCurrentMonthHt}
              iconClassName="text-orange-500"
              iconBgClassName="bg-orange-500/10"
              to={`/refacturation?status=pending&date_from=${refacturationStats.currentMonthDateFrom}&date_to=${refacturationStats.currentMonthDateTo}`}
            />
            <SummaryCard
              icon={CalendarClock}
              label="Refacturation en attente du mois précédent"
              value={displayCount(refacturationStats.pendingPreviousMonthCount)}
              loading={loading}
              period={refacturationStats.previousMonthLabel}
              amountHt={refacturationStats.pendingPreviousMonthHt}
              iconClassName="text-amber-500"
              iconBgClassName="bg-amber-500/10"
              to={`/refacturation?status=pending&date_from=${refacturationStats.previousMonthDateFrom}&date_to=${refacturationStats.previousMonthDateTo}`}
            />
            <SummaryCard
              icon={CalendarRange}
              label="Refacturation en attente de l'année en cours"
              value={displayCount(refacturationStats.pendingCurrentYearCount)}
              loading={loading}
              period={refacturationStats.yearLabel}
              amountHt={refacturationStats.pendingCurrentYearHt}
              iconClassName="text-rose-500"
              iconBgClassName="bg-rose-500/10"
              to={`/refacturation?status=pending&date_from=${refacturationStats.yearDateFrom}&date_to=${refacturationStats.yearDateTo}`}
            />
            <SummaryCard
              icon={History}
              label="Refacturation en attente de l'année précédente"
              value={displayCount(refacturationStats.pendingPreviousYearCount)}
              loading={loading}
              period={refacturationStats.previousYearLabel}
              amountHt={refacturationStats.pendingPreviousYearHt}
              iconClassName="text-fuchsia-500"
              iconBgClassName="bg-fuchsia-500/10"
              to={`/refacturation?status=pending&date_from=${refacturationStats.previousYearDateFrom}&date_to=${refacturationStats.previousYearDateTo}`}
            />
          </div>
          <div className="flex flex-wrap gap-4">
            <SummaryCard
              icon={Check}
              label="Refacturé mois en cours"
              value={displayCount(refacturationStats.refacturedCurrentMonthCount)}
              loading={loading}
              period={refacturationStats.currentMonthLabel}
              amountHt={refacturationStats.refacturedCurrentMonthHt}
              iconClassName="text-green-500"
              iconBgClassName="bg-green-500/10"
              to={`/refacturation?status=refactured&date_from=${refacturationStats.currentMonthDateFrom}&date_to=${refacturationStats.currentMonthDateTo}`}
            />
            <SummaryCard
              icon={CalendarDays}
              label="Refacturé mois précédent"
              value={displayCount(refacturationStats.refacturedPreviousMonthCount)}
              loading={loading}
              period={refacturationStats.previousMonthLabel}
              amountHt={refacturationStats.refacturedPreviousMonthHt}
              iconClassName="text-teal-500"
              iconBgClassName="bg-teal-500/10"
              to={`/refacturation?status=refactured&date_from=${refacturationStats.previousMonthDateFrom}&date_to=${refacturationStats.previousMonthDateTo}`}
            />
            <SummaryCard
              icon={CalendarRange}
              label="Refacturé année en cours"
              value={displayCount(refacturationStats.refacturedCurrentYearCount)}
              loading={loading}
              period={refacturationStats.yearLabel}
              amountHt={refacturationStats.refacturedCurrentYearHt}
              iconClassName="text-emerald-500"
              iconBgClassName="bg-emerald-500/10"
              to={`/refacturation?status=refactured&date_from=${refacturationStats.yearDateFrom}&date_to=${refacturationStats.yearDateTo}`}
            />
            <SummaryCard
              icon={History}
              label="Refacturé année précédente"
              value={displayCount(refacturationStats.refacturedPreviousYearCount)}
              loading={loading}
              period={refacturationStats.previousYearLabel}
              amountHt={refacturationStats.refacturedPreviousYearHt}
              iconClassName="text-lime-500"
              iconBgClassName="bg-lime-500/10"
              to={`/refacturation?status=refactured&date_from=${refacturationStats.previousYearDateFrom}&date_to=${refacturationStats.previousYearDateTo}`}
            />
          </div>
        </DashboardSection>
      </div>
    </div>
  );
};
